import { useEffect, useState, useRef } from "react";
import { getWSUrl } from "../../utils/ws";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getSupportDashboardData } from "../../api/supportDashboard";

const MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const WS_URL = getWSUrl("/ws/support/notifications/");
const socket = new WebSocket(WS_URL);
/* ---------- تولتیپ مشترک نمودار (هم‌ساختار با داشبورد اصلی) ---------- */
const ChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <span className="chart-tooltip-label">{label}</span>
      <span className="chart-tooltip-value">
        {payload[0].value.toLocaleString("fa-IR")}
        {unit ? ` ${unit}` : ""}
      </span>
    </div>
  );
};

const axisTick = { fontSize: 10.5, fill: "rgba(43, 18, 76, 0.5)" };

/* ---------- نمودار میله‌ای تیکت‌های ماهانه (هم‌ساختار با SalesBarChart) ---------- */
const TicketsBarChart = ({ data }) => (
  <div className="chart-container">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 24, right: 4, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id="barFillGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rose-500)" />
            <stop offset="100%" stopColor="var(--mauve-700)" />
          </linearGradient>
        </defs>
        <CartesianGrid
          stroke="rgba(43, 18, 76, 0.09)"
          strokeDasharray="4 4"
          vertical={true}
          horizontal={true}
        />
        <XAxis
          dataKey="month"
          tickFormatter={(m) => MONTHS[m - 1]}
          tickLine={false}
          axisLine={{ stroke: "rgba(43, 18, 76, 0.15)" }}
          tick={axisTick}
          dy={6}
        />
        <YAxis
          axisLine={{ stroke: "rgba(43, 18, 76, 0.15)" }}
          tickLine={false}
          tick={axisTick}
          width={34}
          allowDecimals={false}
          domain={[0, (max) => max * 1.15]}
        />
        <Tooltip
          content={<ChartTooltip unit="تیکت" />}
          cursor={{ fill: "rgba(82, 43, 91, 0.07)" }}
          labelFormatter={(m) => MONTHS[m - 1]}
        />
        <Bar
          dataKey="count"
          fill="url(#barFillGradient)"
          radius={[8, 8, 8, 8]}
          maxBarSize={32}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

function TicketIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
      <path d="M13 5v2M13 17v2M13 11v2" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </svg>
  );
}

function Toast({ notifications, onDismiss }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {notifications.map((n) => (
        <div
          key={n.id}
          onClick={() => onDismiss(n.id)}
          style={{
            background: "#0ea5e9",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            minWidth: 260,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            animation: "slideIn 0.4s ease",
            cursor: "pointer",
          }}
        >
          <span>{n.text}</span>
          <span style={{ marginRight: 12, opacity: 0.7, fontSize: 18 }}>×</span>
        </div>
      ))}
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(60px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

export default function SupportDashboardHome() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const yearRef = useRef(year);

  const fetchData = (y) =>
    getSupportDashboardData({ ticket_year: y }).then((r) => setData(r.data));

  useEffect(() => {
    yearRef.current = year;
    fetchData(year);
  }, [year]);

  // useEffect(() => {
  //   const ws = new WebSocket(WS_URL);

  //   ws.onmessage = (e) => {
  //     const msg = JSON.parse(e.data);
  //     let text;
  //     if (msg.event === "new_ticket") text = `🎫 تیکت جدید: ${msg.title}`;
  //     else if (msg.event === "new_message")
  //       text = `💬 پیام جدید در تیکت: ${msg.title}`;
  //     else if (msg.event === "new_consultation")
  //       text = `📋 ${msg.source_label} جدید از ${msg.full_name}`;
  //     else return;

  //     setNotifications((prev) => [
  //       { id: Date.now(), text },
  //       ...prev.slice(0, 4),
  //     ]);
  //     fetchData(yearRef.current);
  //   };

  //   return () => ws.close();
  // }, []);

  // const dismiss = (id) =>
  //   setNotifications((prev) => prev.filter((n) => n.id !== id));

  if (!data) return <div className="dash">در حال بارگذاری...</div>;

  const { summary, tickets_by_month, available_years } = data;

  return (
    <div className="dash">
      {/* <Toast notifications={notifications} onDismiss={dismiss} /> */}

      <div className="dash-head">
        <h1>داشبورد پشتیبانی</h1>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-top-supporter">
            <div className="stat-icon">
              <TicketIcon />
            </div>
            <div className="stat-body">
              <div className="stat-label">تیکت‌های باز</div>
              <div className="stat-value">{summary.open_tickets}</div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-top-supporter">
            <div className="stat-icon">
              <ClockIcon />
            </div>
            <div className="stat-body">
              <div className="stat-label">در صف پاسخ</div>
              <div className="stat-value">{summary.waiting_tickets}</div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-top-supporter">
            <div className="stat-icon">
              <CheckIcon />
            </div>
            <div className="stat-body">
              <div className="stat-label">تیکت‌های بسته</div>
              <div className="stat-value">{summary.closed_tickets}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>تیکت‌های ثبت‌شده</h2>
          <select
            className="panel-tag"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {available_years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <TicketsBarChart data={tickets_by_month} />
      </div>
    </div>
  );
}
